import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Drawer } from "@mui/material";
import styles from "./customiseTable.module.css";
import CloseIcon from '../../../../assets/images/close-icon.svg'
import ArrowUp from '../../../../assets/images/arrow-up.svg'
import ArrowDown from '../../../../assets/images/arrow-down.svg'
import { getColumnPreferences, saveColumnPreference } from "Samarth Module/api/api";

const drawerPaperSx = {
    background: "transparent",
    boxShadow: "none",
    overflow: "visible",
    width: "auto",
    maxWidth: "100%",
};

const getUserEmail = () => {
    try {
        return JSON.parse(localStorage.getItem("UserInfo"))?.user_info?.email || "";
    } catch (error) {
        console.error("Failed to parse UserInfo from localStorage:", error);
        return "";
    }
};

const isDemandColumn = (column) => String(column?.label || column?.key || "").toLowerCase() === "demand";
const isUomColumn = (column) => String(column?.label || column?.key || "").toLowerCase() === "uom";

const toDrawerColumns = (columns) => {
    const sorted = columns
        .slice()
        .sort((a, b) => (a.DisplayOrder || 0) - (b.DisplayOrder || 0))
        .map((column) => ({
            ...column,
            key: column.key || column.label,
            checked: column.visibility !== false && column.checked !== false,
            hideMoveButtons: String(column.label).toLowerCase() === "demand",
        }));
    const uomIndex = sorted.findIndex(isUomColumn);
    const demandIndex = sorted.findIndex(isDemandColumn);
    if (uomIndex !== -1 && demandIndex !== -1 && uomIndex !== demandIndex - 1) {
        const [uomColumn] = sorted.splice(uomIndex, 1);
        const insertAt = demandIndex > uomIndex ? demandIndex - 1 : demandIndex;
        sorted.splice(insertAt, 0, uomColumn);
    }

    return sorted;

};



const preferenceKey = (column) => {
    const value = String(column?.key || column?.label || "").replaceAll(/[\s_-]+/g, "").toLowerCase();
    if (["site", "sites", "sitecode"].includes(value)) return "site";
    const week = /(?:demand)?week(\d+)/.exec(value);
    return week ? `week${week[1]}` : value;
};

const drawerLabel = (column) => {
    if (preferenceKey(column) === "site") return "SITE";
    const label = column?.label;
    const match = /^demand[\s_-]*week\s*(\d+)$/i.exec(String(label || ""));
    return match ? `Week ${match[1]}` : label;
};

const isDemandWeekColumn = (column) => /^(?:demand[\s_-]*)?week\s*\d+$/i.test(String(column?.label || column?.key || ""));

function CustomiseTable({ open, onClose, onApply, columnPreferences = [], type = "Demand Page" }) {
    const [columns, setColumns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!open) return undefined;
        const email = getUserEmail();
        let cancelled = false;
        setColumns([]);
        setIsLoading(true);
        if (!email) {
            setIsLoading(false);
            return undefined;
        }
        getColumnPreferences(email, type)
            .then((response) => {
                const serverColumns = response?.columns || response?.default_columns;
                if (!cancelled && Array.isArray(serverColumns)) {
                    const currentByKey = new Map(columnPreferences.map((column) => [preferenceKey(column), column]));
                    setColumns(toDrawerColumns(serverColumns).map((column) => {
                        const current = currentByKey.get(preferenceKey(column));
                        if (!current) return column;
                        const checked = column.checked && current.visibility !== false && current.checked !== false;
                        return { ...column, checked };
                    }));
                }
            })
            .catch((error) => {
                console.error("Unable to load column preferences:", error.message);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [open, columnPreferences, type]);

    const toggleColumn = (key) => {
        setColumns((prev) => {
            const selected = prev.find((column) => column.key === key);
            if (!selected) return prev;

            // Demand is a group toggle: changing it also changes every week
            // nested beneath it.
            if (isDemandColumn(selected)) {
                const weekColumns = prev.filter(isDemandWeekColumn);
                if (weekColumns.length === 0) {
                    return prev.map((column) => (
                        column.key === key ? { ...column, checked: !column.checked } : column
                    ));
                }
                const checked = !weekColumns.every((column) => column.checked);
                return prev.map((column) => (
                    isDemandColumn(column) || isDemandWeekColumn(column)
                        ? { ...column, checked }
                        : column
                ));
            }

            const next = prev.map((column) => (column.key === key ? { ...column, checked: !column.checked } : column));
            if (!isDemandWeekColumn(selected)) return next;
            const allWeeksChecked = next.filter(isDemandWeekColumn).every((column) => column.checked);
            return next.map((column) => (
                isDemandColumn(column) ? { ...column, checked: allWeeksChecked } : column
            ));
        });
    };

    const move = (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= columns.length) return;

        const demandIndex = columns.findIndex((c) => String(c.label).toLowerCase() === "demand");

        if (demandIndex === -1) {
            const weeksStartIndex = columns.findIndex(isDemandWeekColumn);
            if (weeksStartIndex !== -1 && (index >= weeksStartIndex) !== (target >= weeksStartIndex)) return;
        } else {
            if (index < demandIndex && target >= demandIndex) return;
            if (index > demandIndex && target <= demandIndex) return;
        }

        const next = [...columns];
        [next[index], next[target]] = [next[target], next[index]];
        setColumns(next);
    };

    const demandColumn = columns.find(isDemandColumn);
    const weekColumns = columns.filter(isDemandWeekColumn);
    const hasSelectedWeeks = weekColumns.some((column) => column.checked);
    const hasUnselectedWeeks = weekColumns.some((column) => !column.checked);
    const displayColumns = demandColumn
        ? columns.reduce((result, column, index) => {
            if (isDemandWeekColumn(column)) return result;
            result.push({ column, index, isChild: false });
            if (isDemandColumn(column)) {
                weekColumns.forEach((weekColumn) => {
                    result.push({ column: weekColumn, index: columns.indexOf(weekColumn), isChild: true });
                });
            }
            return result;
        }, [])
        : columns.map((column, index) => ({ column, index, isChild: false }));

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
        >
            <aside className={styles.drawer}>
                <header className={styles.header}>
                    <h2 className={styles.title}>Customise Table</h2>
                    <button className={styles.closeButton} type="button" onClick={onClose} aria-label="Close">
                        <Box src={CloseIcon} component='img' alt="Close" />
                    </button>
                </header>

                <div className={styles.body}>
                    <p className={styles.description}>
                        Select the columns you wish to display in the table. To adjust their order, use drag and drop or the up and down buttons next to each column.
                    </p>

                    <h3 className={styles.sectionLabel}>Current Selection</h3>

                    <ul className={styles.columnList} aria-busy={isLoading}>
                        {isLoading && <h6 className={styles.columnRow}>Loading data...</h6>}
                        {!isLoading && columns.length === 0 && (
                            <li className={styles.columnRow}>No column preferences available.</li>
                        )}
                        {!isLoading && displayColumns.map(({ column, index, isChild }) => {
                            const isPartiallySelectedDemand = isDemandColumn(column) && hasSelectedWeeks && hasUnselectedWeeks;

                            return (
                                <li
                                    key={column.key}
                                    className={`${styles.columnRow} ${index === 0 ? styles.columnRowActive : ""} ${isChild ? styles.childColumnRow : ""}`}
                                >
                                    <label className={styles.columnLabel}>
                                        <input
                                            type="checkbox"
                                            className={styles.checkboxInput}
                                            checked={column.checked}
                                            aria-checked={isPartiallySelectedDemand ? "mixed" : column.checked}
                                            onChange={() => toggleColumn(column.key)}
                                        />
                                        <span
                                            className={`${styles.checkbox} ${column.checked ? styles.checkboxChecked : ""} ${isPartiallySelectedDemand ? styles.checkboxIndeterminate : ""}`}
                                            aria-hidden="true"
                                        />
                                        <span>{drawerLabel(column)}</span>
                                    </label>
                                    {!column.hideMoveButtons && (
                                        <div className={styles.moveButtons}>
                                            <button
                                                className={styles.moveButton}
                                                type="button"
                                                onClick={() => move(index, -1)}
                                                aria-label={`Move ${drawerLabel(column)} up`}
                                                disabled={index === 0}
                                            >
                                                <Box src={ArrowUp} alt="arrow up" component='img' />
                                            </button>
                                            <button
                                                className={styles.moveButton}
                                                type="button"
                                                onClick={() => move(index, 1)}
                                                aria-label={`Move ${drawerLabel(column)} down`}
                                                disabled={index === columns.length - 1}
                                            >
                                                <Box src={ArrowDown} alt="arrow down" component='img' />
                                            </button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                <footer className={styles.footer}>
                    <button className={styles.cancelButton} type="button" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className={styles.applyButton}
                        type="button"
                        disabled={isSaving || isLoading}
                        onClick={async () => {
                            const email = getUserEmail();
                            const preferences = columns.map((column, index) => ({
                                key: column.key,
                                DisplayOrder: index + 1,
                                label: column.label,
                                sortable: column.sortable !== false,
                                // Demand remains visible when at least one of
                                // its weeks is selected (the blue minus state).
                                // It is hidden only after every week is unchecked.
                                visibility: isDemandColumn(column) ? hasSelectedWeeks : column.checked,
                                checked: isDemandColumn(column) ? hasSelectedWeeks : column.checked,
                            }));
                            setIsSaving(true);
                            try {
                                if (email) await saveColumnPreference(email, type, preferences);
                                onApply?.(preferences);
                                onClose?.();
                            } catch (error) {
                                console.error("Unable to save column preferences:", error.message);
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                    >
                        {isSaving ? "Saving..." : "Apply"}
                    </button>
                </footer>
            </aside>
        </Drawer>
    );
}

CustomiseTable.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onApply: PropTypes.func,
    columnPreferences: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string,
            label: PropTypes.string,
            visibility: PropTypes.bool,
            checked: PropTypes.bool,
        })
    ),
    type: PropTypes.string,
};

export default CustomiseTable;